import React, { type ReactNode } from "react";

import base from "@/themes/base/base";

type Props = {
    name: string;
    children: ReactNode;
    fallback?: ReactNode;
};

type State = {
    hasError: boolean;
    error?: unknown;
    componentStack?: string;
};

export default class ErrorBoundary extends React.Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(error: unknown): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: unknown, info: React.ErrorInfo) {
        this.setState({ error, componentStack: info?.componentStack ?? undefined });
        // This is intentionally noisy and specific, it helps identify the crashing widget quickly.
        // eslint-disable-next-line no-console
        console.error(
            `[SessionDetail widget crash] ${this.props.name}`,
            error,
            info?.componentStack,
        );
    }

    render() {
        if (!this.state.hasError) return this.props.children;

        if (this.props.fallback) return this.props.fallback;

        const formatError = (err: unknown) => {
            if (!err) return null;

            if (err instanceof Error) {
                return {
                    name: err.name,
                    message: err.message,
                    stack: err.stack,
                };
            }

            if (typeof err === "string") return { name: "Error", message: err, stack: undefined };

            try {
                return { name: "Error", message: JSON.stringify(err), stack: undefined };
            } catch {
                return { name: "Error", message: String(err), stack: undefined };
            }
        };

        const details = formatError(this.state.error);
        const hasMoreDetails = Boolean(details?.stack || this.state.componentStack);

        return (
            <div
                style={{
                    display: "flex",
                    width: "100%",
                    height: "100%",
                    flex: 1,
                    alignItems: "stretch",
                }}
            >
                <div
                    style={{
                        borderRadius: 8,
                        padding: "1.5rem",
                        background: base.colors.neutral.grey[800],
                        color: "#F2F2F2",
                        fontSize: 12,
                        lineHeight: 1.4,
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.5rem",
                        width: "100%",
                        flex: 1,
                        boxSizing: "border-box",
                    }}
                >
                    <div style={{ fontWeight: 600 }}>Component Error</div>
                    <div style={{ opacity: 0.8 }}>
                        An error occurred while rendering the {this.props.name} component.
                    </div>

                    {details?.message && (
                        <div style={{ opacity: 0.9, marginTop: "0.5rem", whiteSpace: "pre-wrap" }}>
                            {details.name !== "Error" ? `${details.name}: ` : ""}
                            {details.message}
                        </div>
                    )}
                    {hasMoreDetails && (
                        <details style={{ marginTop: "0.5rem" }}>
                            <summary style={{ cursor: "pointer", opacity: 0.85 }}>
                                Details (stack)
                            </summary>
                            <pre
                                style={{
                                    marginTop: "0.5rem",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    opacity: 0.85,
                                    fontSize: 11,
                                }}
                            >
                                {details?.stack || ""}
                                {details?.stack && this.state.componentStack ? "\n\n" : ""}
                                {this.state.componentStack || ""}
                            </pre>
                        </details>
                    )}
                </div>
            </div>
        );
    }
}
